// ===================== SVG pixel-art sprites =====================
// Sprites are authored as compact character-grid "pixel maps" and emitted as
// run-length-encoded SVG <rect> art (crisp pixel edges), then rasterized to
// offscreen canvases for fast, nearest-neighbor scaled drawing in-game.

const cache = new Map();   // key -> { canvas, w, h }
let readyResolve;
export const spritesReady = new Promise((r) => (readyResolve = r));

// Build an SVG string from a ragged pixel map (rows may differ in length).
function pixSVG(rows, palette, px = 1) {
  let w = 0;
  for (const r of rows) w = Math.max(w, r.length);
  const h = rows.length;
  let rects = '';
  for (let y = 0; y < h; y++) {
    const row = rows[y];
    let x = 0;
    while (x < row.length) {
      const c = row[x];
      const fill = palette[c];
      if (!fill) { x++; continue; }
      let run = 1;
      while (x + run < row.length && row[x + run] === c) run++;
      rects += `<rect x="${x * px}" y="${y * px}" width="${run * px}" height="${px}" fill="${fill}"/>`;
      x += run;
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w * px}" height="${h * px}" viewBox="0 0 ${w * px} ${h * px}" shape-rendering="crispEdges">${rects}</svg>`;
}

function svgToCanvas(svg) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = img.width; c.height = img.height;
      const g = c.getContext('2d');
      g.imageSmoothingEnabled = false;
      g.drawImage(img, 0, 0);
      resolve({ canvas: c, w: img.width, h: img.height });
    };
    img.onerror = () => resolve(null);
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  });
}

export function getSprite(key) { return cache.get(key); }
export function allSpriteKeys() { return Object.keys(MAPS); }

// Self-check for scripts/validate-content.mjs. A character with no palette
// entry is silently skipped by pixSVG — it renders as a hole, not an error —
// so typos here ship as invisible chunks missing from a sprite. A declared but
// unused entry is usually a lost intent (an outline that never got drawn).
// This is exported rather than the raw MAPS so the data stays private.
export function spriteAudit() {
  const out = [];
  for (const [key, m] of Object.entries(MAPS)) {
    if (!m || !Array.isArray(m.rows) || !m.pal) continue;
    const used = new Set();
    for (const row of m.rows) for (const ch of row) if (ch !== ' ') used.add(ch);
    const missing = [...used].filter((c) => !m.pal[c]);
    const unused = Object.keys(m.pal).filter((c) => !used.has(c));
    if (missing.length || unused.length) out.push({ key, missing, unused });
  }
  return out;
}

// ---------- color helpers (derive shading tiers from a base hue) ----------
function _hx(n) { n = Math.max(0, Math.min(255, Math.round(n))); return n.toString(16).padStart(2, '0'); }
function _parse(h) { h = h.replace('#', ''); if (h.length === 3) h = h.split('').map((c) => c + c).join(''); const n = parseInt(h, 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; }
function darken(hex, t) { const [r, g, b] = _parse(hex); return '#' + _hx(r * (1 - t)) + _hx(g * (1 - t)) + _hx(b * (1 - t)); }
function lighten(hex, t) { const [r, g, b] = _parse(hex); return '#' + _hx(r + (255 - r) * t) + _hx(g + (255 - g) * t) + _hx(b + (255 - b) * t); }
function mix(a, b, t) { const A = _parse(a), B = _parse(b); return '#' + _hx(A[0] + (B[0] - A[0]) * t) + _hx(A[1] + (B[1] - A[1]) * t) + _hx(A[2] + (B[2] - A[2]) * t); }

// data-URI (for HUD <img> icons).
const dataUriCache = new Map();
export function spriteDataUri(key) {
  if (dataUriCache.has(key)) return dataUriCache.get(key);
  const def = MAPS[key];
  if (!def) return '';
  const uri = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(pixSVG(def.rows, def.pal, 2));
  dataUriCache.set(key, uri);
  return uri;
}

// ---------- palettes ----------
// Player cloak palette factory (element/character color swaps) with derived
// shading tiers: C deepest shadow, c dark, m mid, v light, r rim highlight.
function playerPal(dark, light, eye) {
  return {
    K: '#0b0b18',
    C: darken(dark, 0.4),
    c: dark,
    m: mix(dark, light, 0.5),
    v: light,
    r: lighten(light, 0.4),
    F: darken(dark, 0.6),
    e: eye,
    E: lighten(eye, 0.5),
    B: '#241a34',
    b: '#140e20',
    h: mix(dark, light, 0.4),
  };
}

// ---------- pixel maps ----------
// Player base silhouette (hooded figure) — reused with palette swaps.
const PLAYER_ROWS = [
  '       KKKK       ',
  '      KrvvmcK      ',
  '     KrvvvmccK     ',
  '    KrvvvmmmccK    ',
  '    KrvFFFFFmcK    ',
  '   KrvFFFFFFFcCK   ',
  '   KrvFeEFFeEFcK   ',
  '   KrvFFFFFFFFcK   ',
  '   KrvvFFFFFFmcK   ',
  '    KrvmmFFmmcK    ',
  '    KrvmmmmmmcK    ',
  '   KrvmmmvmmmccK   ',
  '  KrvmmmmvmmmmccK  ',
  '  KrvmmhmvmhmmccK  ',
  '  KrvmmmmvmmmmccK  ',
  '  KrvvmmmvmmmvccK  ',
  '   KrvmmmvmmmccK   ',
  '   KrvmmmmmmmccK   ',
  '   KKcmmmmmmcKK    ',
  '    KbBBKKBBbK     ',
  '    KbBbK KBBbK    ',
  '    KKKK  KKKK     ',
];

// Enemy pixel maps -----------------------------------------------------------
const MAPS = {
  // --- players (registered by character defs, but keep a default) ---
  player_wanderer: { rows: PLAYER_ROWS, pal: playerPal('#39407a', '#5b66b8', '#8ff6ff') },
  player_pyre:     { rows: PLAYER_ROWS, pal: playerPal('#7a2a1e', '#d1502b', '#ffd24d') },
  player_frost:    { rows: PLAYER_ROWS, pal: playerPal('#1e5a7a', '#3aa8d1', '#c9f6ff') },
  player_storm:    { rows: PLAYER_ROWS, pal: playerPal('#5a4a1e', '#c9a83a', '#fff59a') },
  player_wisp:     { rows: PLAYER_ROWS, pal: playerPal('#2a5a3a', '#4ac97a', '#ccffcc') },
  player_saint:    { rows: PLAYER_ROWS, pal: playerPal('#b8a24a', '#f0e0a0', '#fffbe0') },

  // --- basic enemies ---
  slime: {
    pal: { K: '#0a2410', d: '#2a7a34', G: '#3fae4a', l: '#63e06a', w: '#dfffe0', e: '#0a2410' },
    rows: [
      '     KKKKKK     ',
      '   KKllllllKK   ',
      '  KllllGGllllK  ',
      ' KllGGGGGGGGlK ',
      ' KlGGGGGGGGGlK ',
      'KlGGwwGGGGGGGlK',
      'KlGGwGGGGGGGGlK',
      'KlGGGGGGGGGGGlK',
      'KlGGeeGGGeeGGlK',
      'KdGGeeGGGeeGGdK',
      'KdGGGGGGGGGGGdK',
      'KdGGGdddGdddGdK',
      ' KddGGGGGGGddK ',
      '  KKddddddddKK  ',
      '    KKKKKKKK    ',
    ],
  },
  bat: {
    pal: { K: '#160a1e', D: '#4a2258', P: '#6a2f7e', p: '#9a55b0', h: '#c98adf', e: '#ff3b6b', E: '#ffb0c0' },
    rows: [
      'K             K',
      'KP  KK   KK  PK',
      'KPP KhK KpK PPK',
      'KPPPhpPPPppPPPK',
      ' KPPhppPpppPPK ',
      ' KDPpeEPPPeEpDK',
      ' KDPpppPPPpppDK',
      '  KDPppPPPppDK ',
      '   KDPpppppDK  ',
      '    KDPpppDK   ',
      '     KDPPDK    ',
      '      KDDK     ',
    ],
  },
  brute: {
    pal: { K: '#2a1408', d: '#5a2718', R: '#7a3520', r: '#a8512e', h: '#d0703e', e: '#ffcf4d', t: '#efe0c8' },
    rows: [
      '     KKKKKK     ',
      '   KKrRRRRrKK   ',
      '  KrRRhRRhRRrK  ',
      '  KrReeRReeRRK  ',
      '  KrRRRRRRRRrK  ',
      ' KrRRhRRRRhRRrK ',
      ' KdRRRRRRRRRRdK ',
      ' KdRttRRRRttRdK ',
      ' KdRRRRRRRRRRdK ',
      '  KdRRRhhRRRdK  ',
      '  KdRRddddRRdK  ',
      '  KKdRRKKRRdKK  ',
      '   KddK  KddK   ',
      '   KKKK  KKKK   ',
    ],
  },
  wisp: {
    pal: { K: '#14243a', d: '#3a8ab0', W: '#6fd6ff', w: '#bfefff', h: '#eafbff', e: '#12305a' },
    rows: [
      '    KKKK    ',
      '   KwwwwK   ',
      '  KwWWWWwK  ',
      ' KwWWhhWWwK ',
      ' KwWeWWeWwK ',
      ' KwWWWWWWwK ',
      ' KdWWWWWWdK ',
      '  KdWWWWdK  ',
      '  KdWwWWdK  ',
      '   KWKKWK   ',
      '   Kw  wK   ',
      '    K  K    ',
    ],
  },
  skeleton: {
    pal: { K: '#22222a', d: '#8a8a98', B: '#d8d8e0', b: '#a8a8b8', h: '#f4f4fa', e: '#ff3b6b' },
    rows: [
      '    KKKKK    ',
      '   KBBhBBK   ',
      '  KBBbbbBBK  ',
      '  KBeBBBeBK  ',
      '  KBBbKbBBK  ',
      '   KBbbbBK   ',
      '   KBBKBBK   ',
      '  KbBBBBBbK  ',
      ' KbBBbKbBBbK ',
      ' KBBKBKBKBBK ',
      ' KbBBbKbBBbK ',
      '  KBBK KBBK  ',
      '  KdBK KBdK  ',
      '  KKK   KKK  ',
    ],
  },
  spider: {
    pal: { K: '#0c0c12', d: '#241830', S: '#3a2a4a', s: '#6a4a7a', h: '#9a6ab0', e: '#ff5cf0' },
    rows: [
      'K K        K K',
      ' KKK KKKK KKK ',
      '  KKKSSSSKKK  ',
      ' KsSSSSSSSSsK ',
      'KsSSeeSSeeSSsK',
      'KsSSSSSSSSSSsK',
      'KdSShSSSShSSdK',
      ' KdSSSSSSSSdK ',
      '  KdSSSSSSdK  ',
      ' KKK KddK KKK ',
      'K K   KK   K K',
    ],
  },
  imp: {
    pal: { K: '#2a1400', d: '#8a3512', O: '#c9541e', o: '#ff8a3d', h: '#ffb06a', e: '#fff04d', f: '#ff3b1e' },
    rows: [
      '  f        f  ',
      '  Kf      fK  ',
      '  KOK    KOK  ',
      ' KOOKKOOKKOOK ',
      ' KOooooooooOK ',
      'KOoohOOOOhooOK',
      'KOoeeOOOOeeoOK',
      'KOoooooooooooK',
      'KOooooooooooOK',
      'KdOooooooooOdK',
      ' KdOoK  KoOdK ',
      '  KOoK  KoOK  ',
      '  KdK    KdK  ',
      '  KKK    KKK  ',
    ],
  },
  spawnling: {
    pal: { K: '#0a2410', G: '#5fce5a', l: '#8fff7a', d: '#2a7a34', e: '#0a2410' },
    rows: [
      '  KKKK  ',
      ' KllllK ',
      'KlGGGGlK',
      'KlGeeGlK',
      'KlGeeGlK',
      'KlGGGGlK',
      'KdGGGGdK',
      ' KddddK ',
      '  KKKK  ',
    ],
  },
  charger: {
    pal: { K: '#1a1030', d: '#3a2058', V: '#4a2a7a', v: '#7a4ac0', h: '#a878e0', e: '#ff4d6d', E: '#ff9ab0', p: '#c9a2ff' },
    rows: [
      ' p          p ',
      ' KpK      KpK ',
      ' KVVKK  KKVVK ',
      ' KVvvvvvvvvVK ',
      'KVvvhvvvvhvvVK',
      'KVveEvvvveEvVK',
      'KVvvvvvvvvvvVK',
      'KdVvvvvvvvvvdK',
      ' KdVvvvvvvvdK ',
      '  KVvKKKKvVK  ',
      '  KvK    KvK  ',
      '  KKK    KKK  ',
    ],
  },

  // --- meadow ---
  hornet: {
    pal: { K: '#2a2200', Y: '#ffd83a', y: '#c9a000', B: '#1a1400', h: '#fff0a0', w: '#dfefff', e: '#ff3b1e', E: '#ff9a6a' },
    rows: [
      'ww   K  K   ww',
      ' wwKKyKKyKKww ',
      '  KYhyBByyYK  ',
      ' KYyYBYYBYyYK ',
      'KYyYYBYYBYYYyK',
      'KYByYYYYYYyBYK',
      'KyYeEYYYYeEYyK',
      'KYyYYYYYYYYyYK',
      ' KYByyyyyyBYK ',
      '  KKyBYYByKK  ',
      '    KKyyKK    ',
      '     KBBK     ',
    ],
  },
  sporeling: {
    pal: { K: '#160a26', P: '#7a3aa0', p: '#a86ac9', G: '#2a5a2e', g: '#3f8a44', e: '#d0ffd0' },
    rows: [
      '  KPPPK  ',
      ' KPppppPK',
      'KPpepepPK',
      'KPppppppK',
      ' KPpppPK ',
      '  KGgGK  ',
      '  Kg gK  ',
    ],
  },
  // --- cavern ---
  golem: {
    pal: { K: '#0e1826', d: '#28455f', S: '#3a5a7a', s: '#5a7a9a', h: '#8aa8c8', c: '#7fd6ff', e: '#c9f6ff' },
    rows: [
      '    KKKKKK    ',
      '   KShSSShSK   ',
      '  KSsSSSSSsSK  ',
      '  KSseSSeSSsK  ',
      '  KSSScccSSSK  ',
      ' KSsSSccSSSSsK ',
      ' KSsSSSSSSSSsK ',
      ' KdSShhSShSSdK ',
      ' KdSSSSSSSSSdK ',
      '  KdSSSSSSSdK  ',
      '  KSsKddddKsK  ',
      '  KSsK  KsSK   ',
      '  KKK    KKK   ',
    ],
  },
  lurker: {
    pal: { K: '#070a10', d: '#141c2a', D: '#1a2233', l: '#2e3e52', h: '#465e7a', e: '#ff5cf0', E: '#ffb0f8' },
    rows: [
      '    KKKKK    ',
      '   KDllllDK  ',
      '  KDlllllDK  ',
      ' KDleEleElDK ',
      ' KDlllllllDK ',
      ' KDlhllllhDK ',
      'KDllllllllllK',
      'KdDlllllllDdK',
      ' KdlllllllK  ',
      ' KdlK KKKldK ',
      'KdK  Kd  KdK ',
      'K K  KK  K K ',
    ],
  },
  // --- wastes ---
  hound: {
    pal: { K: '#180800', d: '#7a2810', O: '#c9401e', o: '#ff7a2a', h: '#ffb06a', e: '#ffe14d', E: '#fff0a0', f: '#ff3b1e' },
    rows: [
      'f  K       K  ',
      'KOoK      KoK ',
      'KOooKK  KKooK ',
      'KOoooooooooOK ',
      'KOohoooooohoOK',
      'KOoeEoooeEooOK',
      'KOoooooooooOK ',
      'KdOoooooooOdK ',
      ' KOoKKooKKoOK ',
      ' KoK KooK KoK ',
      ' KKK KKKK KKK ',
    ],
  },
  cinder: {
    pal: { K: '#2a0e00', d: '#8a2e08', O: '#ff5a1e', o: '#ff9a3d', y: '#ffe14d', e: '#3a0e00' },
    rows: [
      '    K     K   ',
      '   KyK   KyK  ',
      '  KyOyK KyOyK ',
      '  KyOOyKyOOyK ',
      '  KyOoooooOyK ',
      '  KOoeoooeoOK ',
      '  KOoooooooOK ',
      '  KdOoooooOdK ',
      '   KdOoooOdK  ',
      '    KdOOOdK   ',
      '     KdddK    ',
      '      KKK     ',
    ],
  },
  wraith: {
    pal: { K: '#14243a', d: '#5a7ab0', W: '#c8d8ff', w: '#8aa8e0', h: '#eef4ff', e: '#5cd9ff' },
    rows: [
      '    KKKK    ',
      '   KWWWWK   ',
      '  KWWhhWWK  ',
      ' KWWeWWeWWK ',
      ' KWWWWWWWWK ',
      ' KwWWWWWWwK ',
      ' KwWWWWWWwK ',
      ' KdWWWWWWdK ',
      '  KwWWWWwK  ',
      '  KwWKKWwK  ',
      '  Kw K KwK  ',
      '   K   K    ',
    ],
  },
  // --- void ---
  voideye: {
    pal: { K: '#0a0014', d: '#3a0e5a', V: '#5a1a8a', v: '#8a3ac0', w: '#e8d8ff', p: '#1a0028', e: '#ff2d9b' },
    rows: [
      '    KKKK    ',
      '  KKVvvvVKK  ',
      ' KVvvvvvvvVK ',
      'KVvvwwwwwvvVK',
      'KVvwweeewwvVK',
      'KVvweppewwvVK',
      'KVvweppewwvVK',
      'KVvwweeewwvVK',
      'KdVvwwwwwvVdK',
      ' KdVvvvvvVdK ',
      '  KKVvvVKK   ',
      '   KKKKKK    ',
    ],
  },
  phantom: {
    pal: { K: '#160a26', d: '#5a3a8a', P: '#c9a2ff', p: '#8a5ac0', h: '#e8d8ff', e: '#ff5cf0', E: '#ffb0f8' },
    rows: [
      '   KKKKKK   ',
      '  KPPPPPPK  ',
      ' KPPhhhhPPK ',
      ' KPeEPPeEPK ',
      ' KPPPPPPPPK ',
      ' KpPPPPPPpK ',
      ' KpPPPPPPpK ',
      ' KpPPPPPPpK ',
      ' KdPPPPPPdK ',
      '  KpPKKPpK  ',
      '  Kp K KpK  ',
      '   K   K    ',
    ],
  },

  // --- bosses (larger, high-detail) ---
  boss_maw: {
    pal: { K: '#160a1e', d: '#3a1020', b: '#b79ac8', l: '#d8c8e8', h: '#fff4ff', R: '#7a1e3a', r: '#c93a5e', m: '#ff5c7e', e: '#ffe14d', E: '#fff7b0', t: '#efe2ee' },
    rows: [
      '    KK          KK    ',
      '   KddK        KddK   ',
      '   KdlK        KldK   ',
      '  KKdlKKKKKKKKKKldKK  ',
      ' KdbllllllllllllllbdK ',
      'KdblllllhhhhhhllllllbdK',
      'KblllllllllllllllllllbK',
      'KbllKKKKKKllKKKKKKllbK',
      'KbllKddddKllKddddKllbK',
      'KbllKdeEdKllKdeEdKllbK',
      'KbllKddddKllKddddKllbK',
      'KbllKKKKKlhhlKKKKKllbK',
      'KdbllllllhhhhllllllbdK',
      ' KdblllllddddllllllbdK',
      ' KdbbllRRRRRRRRRRllbbK',
      '  KdbtKtKtKtKtKtKtbdK ',
      '  KdbRrmrmrmrmrmrRbdK ',
      '  KdbRrmrmrmrmrmrRbdK ',
      '   KdbtKtKtKtKtKtbdK  ',
      '    KdbbRRRRRRRRbbdK   ',
      '     KKdbbbbbbbbdKK    ',
      '       KKKddddKKK      ',
    ],
  },
  boss_warden: {
    pal: { K: '#0a2233', d: '#0e4a6a', I: '#2a7aa8', i: '#5ac9e6', h: '#bff0ff', w: '#eafcff', e: '#ffffff', c: '#dffbff' },
    rows: [
      '      cc    cc      ',
      '     ciIc  ciIc     ',
      '    KKiIKKKKiIKK    ',
      '   KIiiiiiiiiiiiIK   ',
      '  KIiiiwwwwwwiiiIK  ',
      '  KIiwIiiiiiiIwiIK  ',
      ' KIiiweeeeeeeewiiIK ',
      ' KIiiiwwwwwwwwiiiIK ',
      ' KIiiIiiiiiiiiIiiIK ',
      ' KdIiihwwwwwwhiiIdK ',
      '  KIiiIwwwwwwIiiIK  ',
      '  KIiiiiwwwwiiiiIK  ',
      '  KdIiiiiiiiiiIidK  ',
      '   KIiIiwwwwiIiIK   ',
      '   KdIiiiiiiiiIdK   ',
      '    KIiIKKKKIiIK    ',
      '    KIiK    KiIK    ',
      '    KdIK    KIdK    ',
      '     KKK    KKK     ',
    ],
  },
  boss_void: {
    pal: { K: '#0a0014', d: '#1a0033', V: '#3a0a6a', v: '#7a2ac0', h: '#b98cff', p: '#c98aff', e: '#ff5cf0', E: '#ffb0f8', s: '#22004a' },
    rows: [
      '  p    pp    pp  p  ',
      ' KVK  KVVK  KVVK KVK ',
      'KVvvKKVvvVKKVvvVKvvVK',
      'KVvvvVvvvvvVvvvvvvvVK',
      'KVvvhvvvvvvvvvvhvvvVK',
      'KVveEvvvvvvvvvveEvvVK',
      'KVvvvvvhhvvhhvvvvvvVK',
      'KVvvvvvvvvvvvvvvvvvVK',
      'KVvssvvveEeEvvvssvvVK',
      'KVvsseeEEEEeessvvvVK ',
      'KVvvsseEeEeEssvvvvVK ',
      'KVvvvsssssssvvvvvvVK ',
      'KdVvvvvvvvvvvvvvvVdK ',
      ' KdVvvhhhhhhhvvvVdK  ',
      '  KdVvvvvvvvvvvVdK   ',
      '   KVvKKKKKKKKvVK    ',
      '   KvK        KvK    ',
      '   KKK        KKK    ',
    ],
  },
  boss_reaper: {
    pal: { K: '#08080f', d: '#141420', C: '#2a2a3e', c: '#3e3e56', F: '#4a4a66', v: '#5a5a76', b: '#c0c4d4', l: '#eef0f6', e: '#7dff9c', E: '#c8ffda', s: '#9a7a4a', S: '#c8b488' },
    rows: [
      '            KK      ',
      '           KlK      ',
      '   KKKK   KllK      ',
      '  KCccCK KlllbK     ',
      ' KCcccccKKllbbK     ',
      ' KCcvvvcCKlbb sK    ',
      ' KCcFFFFcC Kb sK    ',
      ' KCFbbbbFCK  sK     ',
      ' KCFbeEbFCK  sK     ',
      ' KCFbbbbFCK  sK     ',
      ' KCcFbbFcCK  sK     ',
      '  KCcvvvcCK  sK     ',
      '  KCcdddcCKK sK     ',
      ' KCccvcccccCKsSK    ',
      ' KCcvccccvcccSSK    ',
      'KCcccccccccccKSK    ',
      'KCcvcccccvcccCsK    ',
      'KCccccccccccccsK    ',
      ' KCcccvccccccCsK    ',
      ' KKCcccccccccCsK    ',
      '   KKCdcccdCKKsK    ',
      '    K KK KK K KK    ',
    ],
  },
  // Crystal Cavern finale — THE SHARD REGENT. A faceted crystal monarch:
  // floating shard crown, gem head, diamond torso around a white-hot core,
  // tapering to a levitating point. Cavern cyan (matches the `crystal` decor)
  // with violet deep-shadow facets pulled from the cavern's purple fog.
  boss_shardregent: {
    pal: {
      K: darken('#3a8ad1', 0.85),          // outline
      V: darken('#7a3ac0', 0.35),          // violet deep-shadow facets
      v: mix('#3a8ad1', '#7a3ac0', 0.55),  // blue-violet shadow tier
      C: '#3a8ad1',                        // base (crystal decor hue)
      c: '#7fd6ff',                        // light tier
      h: lighten('#7fd6ff', 0.5),          // rim highlight (top-left)
      w: '#eafcff',                        // white-hot core / crown tips / eyes
    },
    rows: [
      '   c   c   w   v   v',
      '  KcK KcK KwK KvK KvK',
      ' KhccCCCCCCCCCCCCCCvvK',
      '   KhcCCCCCCCCCCCvVK',
      '     KhcCCCCCCCvVK',
      '     KcKwwwVwwwKvK',
      '     KcCCCCCCCCCvK',
      '      KcCCCwCCCvK',
      '        KcCCCvK',
      '         KcCvK',
      '       KKKKKKKKK',
      '    KhccCCCCCCCvvVK',
      ' KhcCCCCCcwwwcCCCCCvVK',
      'KhcCCCCCCcwhwcCCCCCCvVK',
      'KcCCCCCCCcwhwcCCCCCCCvK',
      ' KcCCCCCCCcwcCCCCCCCvK',
      '  KcCCCCCCCwCCCCCCCvK',
      '   KcCCCCVCCCVCCCCvK',
      '    KcCCCVCCCVCCCvK',
      '     KcCCVCCCVCCvK',
      '       KcCVCVCvK',
      '         KcVvK',
      '          KVK',
    ],
  },
  // Ashen Wastes finale — THE ASH TYRANT. A hulking molten behemoth: cracked
  // obsidian hide over ember seams, smoldering gold eyes sunk between massive
  // shoulders, knuckled fists dragging the ground.
  //
  // The hide is cooled basalt crust, not brown rock. The Wastes' ground is
  // `#2a1a14` at luminance ~29; an earlier warm-brown hide sat at 39 with its
  // shadow tier at 28, so half the body was literally floor-coloured and the
  // silhouette dissolved in a frame full of ember particles. The base is now
  // ash grey at 63 — still dark, but it parts from the ground on value, and
  // the volcano comes from the seams instead of the hide. The rim runs the
  // whole top-left contour and the seams are routed out to the silhouette
  // edge (shoulder caps, outer arms, knuckles, feet) so the boundary itself
  // glows rather than relying on the outline alone.
  boss_ashtyrant: {
    pal: {
      K: darken('#4a3c42', 0.76),                       // outline
      D: darken('#4a3c42', 0.42),                       // deep shadow (bottom-right)
      O: '#4a3c42',                                     // cooled-crust obsidian base
      o: lighten('#4a3c42', 0.2),                       // light tier
      r: mix(lighten('#4a3c42', 0.35), '#ff7a2e', 0.62), // ember-lit rim (top-left)
      e: mix('#4a3c42', '#ff5a1e', 0.62),               // banked seam
      E: '#ff5a1e',                                     // open seam (cinder hue)
      y: '#ffd15c',                                     // molten cores + smoldering eyes
    },
    rows: [
      '                   KKKKKKKK',
      '                  KKrrrrrrKK',
      '                 KKroooooorKK',
      '                 KrDDDDDDDDrK',
      '                 KryyDDDDyyDK',
      '                 KroeEEEEeODK',
      '              KKKKKrDDDDDDDKKKKK',
      '         KKKKKKrrrroOOOOOOOrrrrKKKKKK',
      '     KKKKKrrrrrooooOOOOOOOOoooorrrrrKKKKK',
      '   KKKreeeeeeooOOOOOOOOOOOOOOOOooeeeeeerKKK',
      '   KEEEEooOOOOOOOOOOOOOOOOOOOOOOOOOOooEEEEK',
      '   KroOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOOoDK',
      '   KreeeOOOODDOOeeeOOOOOOOOOOOOODDOOOOeeeDK',
      '   KroOOOOODKKroOOOOOOOOOOOOOOODKKroOOOOODK',
      '  KKeoOOOOODKKroOOOOOOOOOOOeeeODKKroOOOOOeKK',
      '  KeeOOOOOODKKroOOOOOOeeOOOOOOODKKroOOOOOeeK',
      '  KeeOOOOOODKKroOOOOOeeeeOOOOOODKKroOOOOOeeK',
      '  KeeOOOOOODKKroOOOOEEyyEEOOOOODKKroOOOOOeeK',
      ' KKroOOOOOODKKeeeOOEEyyyyEEOOeeeKKroOOOOOODKK',
      ' KEEEOOOOOODKKroOOOOEEyyEEOOOOODKKroOOOOOEEEK',
      ' KroOOOOOOODKKroOOOOOeeeeOOOOOODKKroOOOOOOODK',
      ' KroOOOOOOODKKroOOOOOOeeOOOOOOODKKroOOOOOOODK',
      'KKeeeOOOOOODKKKroeeOOOOOOOOeeODKKKroOOOOOeeeKK',
      'KroOOOOOOOODK KroOOOOODDOOOOOODK KroOOOOOOOOrK',
      'KoOOOOOOOOODK KoOOOOODKKoOOOOODK KoOOOOOOOOODK',
      'KEEEEOOOOOODKKKoOOOOODKKoOOOOODKKKoOOOOOOEEEEK',
      'KKoeeeeDDDDKKKoeeeeeeDKKoeeeeeeoKKKoDDDeeeeDKK',
      ' KKKKKKKKKKK KEEEEEEEEKKEEEEEEEEK KKKKKKKKKKK',
      '             KKKKKKKKKKKKKKKKKKKK',
    ],
  },
  // Void finale — THE NULL ARCHON. A tall rift-being: horned hood, a triple
  // gaze (void-pupiled white eye flanked by magenta motes), and a chest torn
  // open on pure nothing — a negative-space core rimmed white-hot. Robe flares
  // to a ragged, unravelling hem. Void purples with the game's magenta accent.
  boss_nullarchon: {
    pal: {
      K: darken('#6a24a0', 0.88),                    // outline
      n: '#030008',                                  // the null — deeper than any outline
      D: darken('#6a24a0', 0.42),                    // deep shadow
      P: '#6a24a0',                                  // base violet
      p: lighten('#6a24a0', 0.22),                   // light tier
      h: mix(lighten('#6a24a0', 0.5), '#ff5cf0', 0.35), // pale orchid rim
      m: '#ff5cf0',                                  // magenta motes / runes
      w: '#fff6ff',                                  // white-hot accent
    },
    rows: [
      ' KhhK           KppK',
      '  KhhK         KppK',
      '   KhhK   h   KppK',
      '    KhhK KhK KppK',
      '     KhhppppppDK',
      '     KhpPPPPPpDK',
      '     KpPPPPPPPDK',
      '     KpmPwwwPmDK',
      '     KpPPwnwPPDK',
      '     KpPPPwPPPDK',
      '      KpPPPPPDK',
      '        KpPDK',
      ' m KpPPPPPPPPPPPDK m',
      '   KpPPPPwwwPPPPDK',
      '  KpPPPwwnnnwwPPPDK',
      ' KpPPPwnnnnnnnwPPPDK',
      ' KpPPwnnnnwnnnnwPPDK',
      ' KpPPPwnnnnnnnwPPPDK',
      '  KpPPPwwnnnwwPPPDK',
      '   KpPPPPwwwPPPPDK',
      ' KpPPPPPPPPPPPPPPPDK',
      ' KpPPmPPPPPPPPPmPPDK',
      'KpPPPPPPPPPPPPPPPPPDK',
      'KpPPDPPPPPPPPPPPDPPDK',
      'KpPPDPPPPPPPPPPPDPPDK',
      'KKPKKPPKPPPPPKPPKKPKK',
      ' KK   KpK   KPK   KK',
    ],
  },

  // --- key art (the NEONFANG wolf) ---
  // The brand mark: title-screen emblem, PWA icon, and both native launcher
  // icons. It is read at 48px more often than anywhere else, so the shape is
  // built out of the cues that survive that: broad-based ears angled out from a
  // flat crown (upright triangles read as a cat), a long tapered snout carrying
  // the bottom third, a fur ruff breaking the cheek line, and fangs big enough
  // to hang past the jaw so they still register in pure silhouette.
  emblem_fang: {
    pal: {
      K: '#0a0a18', D: '#10203f', c: '#1b3e6b', m: '#2d6ba6', v: '#4fb7e8',
      r: '#a9f2ff', W: '#f6ffff', w: '#b9e4f0', e: '#ff5cf0', E: '#ffc4fa',
      M: '#3c0e34', n: '#131c3a',
    },
    rows: [
      '   KrmK                            KmrK',
      '   KrmmK                          KmmrK',
      '  KrvmmmK                        KmmmvrK',
      '  KrvDDDmK                      KmDDDvrK',
      '  KrvDDDDmK      KrvmmvrK      KmDDDDvrK',
      '  KrvDDDDDmK    KrvmmmmvrK    KmDDDDDvrK',
      '  KrvDDDDDDmK  KrvmmmmmmvrK  KmDDDDDDvrK',
      '  KrvDDDDDDDmKKrvmmmmmmmmvrKKmDDDDDDDvrK',
      ' KrvmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmmvrK',
      'KrvmmmmmmmmmmmmmmmmvrrvmmmmmmmmmmmmmmmmvrK',
      'KrvmmmmmmmmmmmmmmmmvrrvmmmmmmmmmmmmmmmmvrK',
      'KrvmmmmmmmmmmmmmmmmvrrvmmmmmmmmmmmmmmmmvrK',
      'KrvmmmmmmmmmmmmmmmmvrrvmmmmmmmmmmmmmmmmvrK',
      'KrvmmmmmmmmmmmmmmmmvrrvmmmmmmmmmmmmmmmmvrK',
      'KrvmmDDDDDDDDmmmmmmvrrvmmmmmmDDDDDDDDmmvrK',
      'KrvDDDDDDDDDDDDmmmmvrrvmmmmDDDDDDDDDDDDvrK',
      'KrvKKKKKKKKKKKDDmmmvrrvmmmDDKKKKKKKKKKKvrK',
      'KrvKEeeeeeKKKKKmmmmvrrvmmmmKKKKKeeeeeEKvrK',
      'KrvmKeeeeeeeKKKKmmmvrrvmmmKKKKeeeeeeeKmvrK',
      'KrvmmmKKeeeeeKKKmmmvrrvmmmKKKeeeeeKKmmmvrK',
      'KrvmmmmmKKKKKKKmmmmvrrvmmmmKKKKKKKmmmmmvrK',
      ' KrvmmmmmmmmmmcmvvvvrrvvvvmcmmmmmmmmmmvrK',
      ' KrvmmmmmmmmmmcmvvvvrrvvvvmcmmmmmmmmmmvrK',
      'KrvmmmmmmmmmmcmvvvvvrrvvvvvmcmmmmmmmmmmvrK',
      '    KrvmmmmmmcmvvvvvrrvvvvvmcmmmmmmvrK',
      '   KrvmmmmmmmcmvvvKKKKKKvvvmcmmmmmmmvrK',
      '  KrvmmmmmmmcmvvvKnnnnnnKvvvmcmmmmmmmvrK',
      '      KrvmmmcmvvKnnnnnnnnKvvmcmmmvrK',
      '       KrvmcmvvKnnnnnnnnnnKvvmcmvrK',
      '        KrvcmvvKnnnnnnnnnnKvvmcvrK',
      '        KrcmvvvKKKKKKKKKKKKvvvmcrK',
      '        KrKWWWWWwMMMMMMMMwWWWWWKrK',
      '        KrKWWWWWwMWMMMMWMwWWWWWKrK',
      '         KKWWWWwMWWMMMMWWMwWWWWKK',
      '          KWWWWwMMMMMMMMMMwWWWWK',
      '          KWWWwccccccccccccwWWWK',
      '          KWWWwKKKKKKKKKKKKwWWWK',
      '          KWWWK            KWWWK',
      '          KWWK              KWWK',
      '          KWWK              KWWK',
      '          KWK                KWK',
    ],
  },

  // --- key art (title/loading wordmark — NEON in cyan, FANG in magenta with
  // white fang descenders; generated letterforms baked to a static map) ---
  wordmark_neonfang: {
    pal: {
      K: '#0a0a18',
      h: '#eaffff', a: '#a9f2ff', b: '#7df9ff', c: '#3ec1e8', d: '#1f7fa8',
      H: '#ffe6fb', A: '#ffc4fa', B: '#ff5cf0', C: '#d83ac8', D: '#8f2394',
      W: '#f6ffff', w: '#b9e4f0',
    },
    rows: [
      ' KKKKK  KKKKKKKKKKKKKKKKK  KKKKKKKKKKK  KKKKK  KKKKKKKKKKKKKKKKK   KKKKKKKKK   KKKKK  KKKKK KKKKKKKKKKK',
      'KKhhhK  KhhhKKhhhhhhhhhhKKKKhhhhhhhhhKKKKhhhK  KhhhKKHHHHHHHHHHKK  KHHHHHHHK  KKHHHK  KHHHKKKHHHHHHHHHKK',
      'KhhhhKK KhhhKKhhhhhhhhhhhKKhhhhhhhhhhhKKhhhhKK KhhhKKHHHHHHHHHHHKKKKHHHHHHHKKKKHHHHKK KHHHKKHHHHHHHHHHHK',
      'KaaaaaK KaaaKKaaaaaaaaaaaKKaaaaaaaaaaaKKaaaaaK KaaaKKAAAAAAAAAAAKKAAAAAAAAAAAKKAAAAAK KAAAKKAAAAAAAAAAAK',
      'KaaaaaKKKaaaKKaaaKKKKKKKKKKaaaKKKKKaaaKKaaaaaKKKaaaKKAAAKKKKKKKKKKAAAKKKKKAAAKKAAAAAKKKAAAKKAAAKKKKKKKKK',
      'KaaaKaaKKaaaKKaaaK        KaaaK   KaaaKKaaaKaaKKaaaKKAAAK        KAAAK   KAAAKKAAAKAAKKAAAKKAAAK',
      'KbbbKbbKKbbbKKbbbKKKKKKK  KbbbK   KbbbKKbbbKbbKKbbbKKBBBKKKKKKK  KBBBK   KBBBKKBBBKBBKKBBBKKBBBK',
      'KbbbKKbbKbbbKKbbbbbbbbbK  KbbbK   KbbbKKbbbKKbbKbbbKKBBBBBBBBBK  KBBBKKKKKBBBKKBBBKKBBKBBBKKBBBK KKKKKKK',
      'KbbbKKbbKbbbKKbbbbbbbbbK  KbbbK   KbbbKKbbbKKbbKbbbKKBBBBBBBBBK  KBBBBBBBBBBBKKBBBKKBBKBBBKKBBBK KBBBBBK',
      'KbbbKKKbbbbbKKbbbbbbbbbK  KbbbK   KbbbKKbbbKKKbbbbbKKBBBBBBBBBK  KBBBBBBBBBBBKKBBBKKKBBBBBKKBBBK KBBBBBK',
      'KcccK KcccccKKcccKKKKKKK  KcccK   KcccKKcccK KcccccKKCCCKKKKKKK  KCCCCCCCCCCCKKCCCK KCCCCCKKCCCK KCCCCCK',
      'KcccK KKccccKKcccKKKKKKKKKKcccKKKKKcccKKcccK KKccccKKCCCK        KCCCKKKKKCCCKKCCCK KKCCCCKKCCCKKKKKCCCK',
      'KcccK  KccccKKcccccccccccKKcccccccccccKKcccK  KccccKKCCCK        KCCCK   KCCCKKCCCK  KCCCCKKCCCCCCCCCCCK',
      'KdddK  KKdddKKdddddddddddKKdddddddddddKKdddK  KKdddKKDDDK        KDDDK   KDDDKKDDDK  KKDDDKKDDDDDDDDDDDK',
      'KdddK   KddKKKddddddddddKKKKdddddddddKKKdddK   KddKKKDDDK        KDDDK   KDDDKKDDDK   KDDKKKKDDDDDDDDDKK',
      'KKKKK   KKKK KKKKKKKKKKKK  KKKKKKKKKKK KKKKK   KKKK KWWWK        KWWWK   KKKKKKWWWK   KKKK KWWWKKKKKKKK',
      '                                                    KWWWK        KWWWK        KWWWK        KWWWK',
      '                                                    KWWKK        KWWKK        KWWKK        KWWKK',
      '                                                    KwKK         KwKK         KwKK         KwKK',
      '                                                    KKK          KKK          KKK          KKK',
    ],
  },

  // --- pickups ---
  chest: {
    pal: { K: '#2a1800', d: '#5a3810', W: '#8a5a20', w: '#c9902e', g: '#ffd15c', h: '#ffe9a0' },
    rows: [
      '  KKKKKKKKKK  ',
      ' KwggggggggwK ',
      'KWwwwwwwwwwwWK',
      'KWwgKgKgKgwwWK',
      'KKKKKKKKKKKKKK',
      'KWwwwwggwwwwWK',
      'KWwwwwghhwwwWK',
      'KWwwwwggwwwwWK',
      'KWwwwwwwwwwwWK',
      'KKKKKKKKKKKKKK',
      ' KdWWWWWWWWdK ',
      '  KKKKKKKKKK  ',
    ],
  },
  heart: {
    pal: { K: '#3a0010', d: '#a01030', R: '#ff2d55', r: '#ff6f91', h: '#ffd0dc' },
    rows: [
      '  KKK    KKK  ',
      ' KRRRK  KRRRK ',
      'KRhrRRKKRRRRRK',
      'KRhRRRRRRRRRRK',
      'KRRRRRRRRRRRRK',
      'KRRRRRRRRRRRRK',
      'KdRRRRRRRRRRdK',
      ' KdRRRRRRRRdK ',
      '  KdRRRRRRdK  ',
      '   KdRRRRdK   ',
      '    KdRRdK    ',
      '     KddK     ',
    ],
  },
  magnet: {
    pal: { K: '#101020', d: '#3a2f4a', R: '#ff3b3b', r: '#ff8a8a', S: '#c0c8e0', s: '#8890b0', h: '#eef0ff' },
    rows: [
      ' KRRRK  KSSSK ',
      'KRhrRK  KShSK',
      'KRrrRK  KSsSK',
      'KRrrRK  KSsSK',
      'KRrrRK  KSsSK',
      'KRrrRKKKKSsSK',
      'KRrrrrrssssSK',
      'KdRrrrrssssdK',
      ' KdRRRSSSSdK ',
      '  KKKKKKKKK  ',
    ],
  },
  bomb: {
    pal: { K: '#050510', d: '#1a1a2a', D: '#2a2a3a', l: '#4a4a60', h: '#8a90b0', f: '#ff8a3d', y: '#ffe14d', g: '#3a3a4e' },
    rows: [
      '        yf   ',
      '        fy   ',
      '       yf    ',
      '    ggKf     ',
      '   KKDDKK    ',
      '  KDDllDDK   ',
      ' KDDllllDDK  ',
      'KDlhlllllllDK',
      'KDlllllllllDK',
      'KDlllllllllDK',
      'KdDlllllllDdK',
      ' KDDllllllDK ',
      '  KKDDDDDKK  ',
      '    KKKKK    ',
    ],
  },
  multiply: {
    pal: { K: '#3a2a00', d: '#8a6010', G: '#c9902e', g: '#ffd15c', h: '#fff6c0' },
    rows: [
      '   KKKKKKKK   ',
      '  KGgggggggK  ',
      ' KGKgg  ggKGK ',
      'KGgKgg  ggKgGK',
      'KGggKg  gKggGK',
      'KGgggKggKgggGK',
      'KGgggghhgggggK',
      'KGgggKggKgggGK',
      'KGggKg  gKggGK',
      'KGgKgg  ggKgGK',
      ' KGKgg  ggKGK ',
      '  KdGggggGdK  ',
      '   KKKKKKKK   ',
    ],
  },

  // --- decorations (biome flavor) ---
  rock: {
    pal: { K: '#1a1a26', d: '#2a2a3a', G: '#3a3a4e', g: '#55556e', h: '#6a6a86' },
    rows: ['   KKKKK   ', '  KGghhgGK ', ' KGgggggGK ', 'KGgggggggGK', 'KGgggggggGK', 'KdGgggggGdK', ' KddGGGddK ', '  KKKKKKK  '],
  },
  bush: {
    pal: { K: '#0e2010', d: '#1a3a1c', G: '#2a5a2e', g: '#3f8a44', h: '#5fae54' },
    rows: ['  KGK  KGK  ', ' KGgGKKGgGK ', 'KGghggghggGK', 'KGgggggggggK', 'KGgggggggggK', 'KdGgggggggdK', ' KdGgggggdK ', '  KG K KGK  '],
  },
  crystal: {
    pal: { K: '#101a2a', d: '#1e5a86', C: '#3a8ad1', c: '#7fd6ff', h: '#dffbff' },
    rows: ['    Kh    ', '   KchK   ', '   KccK   ', '  KCccCK  ', '  KCchCK  ', ' KCcccCK  ', ' KCccccCK ', 'KCCcccCCK ', 'KdCcccCdK ', ' KdCCCdK  ', '  KKKKK   '],
  },
  flower: {
    pal: { K: '#2a1a2a', P: '#ff5cf0', p: '#ffb0f0', y: '#ffe14d', h: '#fff0a0', G: '#2a5a2e', g: '#3f8a44' },
    rows: ['  PpP   ', ' PpPpP  ', 'PpyhyPp ', 'PpyyyPp ', ' PpPpP  ', '  KgK   ', '  KGK   ', ' KGgGK  ', '  KKK   '],
  },
  mushroom: {
    pal: { K: '#2a0e14', d: '#8a1c2e', R: '#c9304a', r: '#ff6a80', w: '#ffe0e0', s: '#d8c8b0', S: '#a89878' },
    rows: ['   KKKK   ', '  KRrrRK  ', ' KRrwwrRK ', 'KRrwrrwrRK', 'KRrrrrrrRK', 'KdRrrrrRdK', ' KKssssKK ', '  KsSSsK  ', '  KsSSsK  ', '  KKssKK  '],
  },
  bones: {
    pal: { K: '#22222a', d: '#8a8a98', B: '#d8d8e0', b: '#a8a8b8', h: '#f0f0f6' },
    rows: ['K B    B K ', 'KBhB  KBbBK', ' Kb    bK  ', '  KBBBBK   ', ' KBbBBbBK  ', 'KdK KK KdK '],
  },
  pillar: {
    pal: { K: '#1a1420', d: '#2a2038', S: '#4a3a5a', s: '#6a5a7a', h: '#8a7a9a', c: '#3a8ad1' },
    rows: [' KSssSK ', 'KSshhsSK', 'KSscssSK', 'KSsssssK', 'KSscssSK', 'KSsssssK', 'KSshssSK', 'KSsssssK', 'KdSsssdK', 'KKKKKKKK', 'KSsssssK', 'KdSsssdK', ' KKKKKK '],
  },
};

// Register a runtime sprite (e.g., generated character palettes) before load.
export function definePlayerSprite(key, dark, light, eye) {
  MAPS[key] = { rows: PLAYER_ROWS, pal: playerPal(dark, light, eye) };
}

export async function loadSprites(onProgress) {
  const entries = Object.entries(MAPS);
  let done = 0;
  await Promise.all(entries.map(async ([key, def]) => {
    const svg = pixSVG(def.rows, def.pal, 1);
    const res = await svgToCanvas(svg);
    if (res) cache.set(key, res);
    done++;
    if (onProgress) onProgress(done / entries.length);
  }));
  readyResolve();
}
